import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';
import ReplayIcon from '@mui/icons-material/Replay';
import { Box, Fade, Tooltip, Typography } from '@mui/material';
import { useCallback, useContext } from 'react';

import { LocaleContext } from '@/Contexts/LocaleContext';

import { Spinner } from '../common/Spinner';

export default function SavedIndicator({
  saved,
  saveFailed,
  isSaving,
  sx,
  saveFn,
}: {
  saved: boolean;
  saveFailed?: boolean;
  isSaving: boolean;
  sx?: object;
  saveFn?: () => void;
}) {
  const { translatedText } = useContext(LocaleContext);
  const handleRetry = useCallback(() => {
    if (saveFn != null) {
      saveFn();
    }
  }, [saveFn]);

  return (
    <Box sx={{ ...sx, ml: 1 }} aria-live='polite'>
      <Typography variant='body2' color='text.secondary' component='span'>
        <Fade in={isSaving} timeout={{ enter: 150, exit: 0 }} mountOnEnter unmountOnExit>
          <span>
            <Spinner size={20} type='circular' aria-label={translatedText.common.ariaSaving} />
          </span>
        </Fade>

        <Fade
          in={saved && !isSaving && !saveFailed}
          timeout={{ enter: 1000, exit: 0 }}
          mountOnEnter
          unmountOnExit
        >
          <span>
            <CheckIcon
              sx={{
                color: 'success.main',
                fontSize: 24,
                marginBottom: '.05rem',
              }}
              aria-label={translatedText.common.ariaSaved}
              role='img'
            />
          </span>
        </Fade>
        <Fade
          in={saved && !isSaving && saveFailed}
          timeout={{ enter: 1000, exit: 0 }}
          mountOnEnter
          unmountOnExit
        >
          <span>
            <span>
              <Tooltip title={translatedText.common.tooltipSaveFailed}>
                <ErrorIcon
                  sx={{
                    color: 'error.main',
                    fontSize: 24,
                    marginBottom: '.05rem',
                  }}
                  aria-label={translatedText.common.ariaSaveFailed}
                  role='img'
                />
              </Tooltip>
            </span>

            {/* // Add a retry icon next to the error icon
            // Add on hover text "Retry save" */}
            {saveFn != null && (
              <span>
                <Tooltip title={translatedText.common.tooltipRetrySave}>
                  <ReplayIcon
                    sx={{
                      fontSize: 24,
                      marginLeft: '5px',
                      marginBottom: '.05rem',
                      cursor: 'pointer',
                    }}
                    onClick={handleRetry}
                    aria-label={translatedText.common.tooltipRetrySave}
                    role='img'
                  />
                </Tooltip>
              </span>
            )}
          </span>
        </Fade>
      </Typography>
    </Box>
  );
}
